/**
 * WelcomeMicroDelight
 * --------------------
 * Plays once per user, per device, the very first time they reach a
 * MangoCubs dashboard after logging in. It overlays a soft, semi-transparent
 * panel with the cartoon mango bouncing in and a single warm greeting:
 *
 *   • MangoCubs internal users (super_admin, hq_admin)
 *       → "Welcome to MangoCubs™ 👋"
 *   • Everyone else
 *       → "Welcome to {SchoolName} powered by MangoCubs™ 👋"
 *   • If the school's co-branding hasn't loaded (or display_name is blank),
 *     we fall back to the internal greeting.
 *
 * Auto-dismisses after 4.8s; clicking anywhere also dismisses it.
 * Tracks `mc_welcome_seen_<user_id>` in localStorage so each user only sees
 * it once on a given device.
 */
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { useBranding } from "@/contexts/BrandingContext";

const INTERNAL_ROLES = new Set(["super_admin", "hq_admin"]);
// Admin-tier roles get a faster auto-dismiss so the modal doesn't
// linger over their work queue. Family roles enjoy a slower beat.
const ADMIN_ROLES = new Set([
  "super_admin", "hq_admin", "franchise_hq_admin",
  "branch_admin", "principal", "academic_coordinator",
  "counsellor", "teacher",
]);
const AUTO_DISMISS_MS_ADMIN = 2500;
const AUTO_DISMISS_MS = 4800;

export default function WelcomeMicroDelight() {
  const { user } = useAuth();
  const { brand, isCoBranded } = useBranding();
  const [open, setOpen] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    if (!user) return;
    const key = `mc_welcome_seen_${user.user_id}`;
    try {
      if (localStorage.getItem(key)) return;
      // Give the dashboard 600 ms to paint before celebrating, so the user
      // sees their data appear behind the overlay.
      const t = setTimeout(() => {
        setOpen(true);
        localStorage.setItem(key, "1");
      }, 600);
      return () => clearTimeout(t);
    } catch (_) {
      /* localStorage blocked — still show */
      setOpen(true);
    }
  }, [user]);

  useEffect(() => {
    if (!open) return;
    const ms = ADMIN_ROLES.has(user?.role) ? AUTO_DISMISS_MS_ADMIN : AUTO_DISMISS_MS;
    const t = setTimeout(() => setDismissing(true), ms);
    return () => clearTimeout(t);
  }, [open, user?.role]);

  if (!user || !open) return null;

  const isInternal = INTERNAL_ROLES.has(user.role);
  const schoolName = brand?.display_name?.trim();
  const showCoBrand = !isInternal && isCoBranded && schoolName;

  const greetingNode = showCoBrand ? (
    <>
      Welcome to <span className="font-bold text-terracotta">{schoolName}</span>
      <span className="block sm:inline">
        {" "}<span className="text-ink-soft text-base sm:text-lg font-normal">powered by</span>{" "}
        <span className="font-bold">MangoCubs<sup className="text-sm align-super">™</sup></span>
      </span>{" "}<span className="inline-block">👋</span>
    </>
  ) : (
    <>
      Welcome to <span className="font-bold">MangoCubs<sup className="text-sm align-super">™</sup></span>{" "}
      <span className="inline-block">👋</span>
    </>
  );

  const close = () => setDismissing(true);

  return (
    <AnimatePresence onExitComplete={() => { setOpen(false); setDismissing(false); }}>
      {!dismissing && (
        <motion.div
          key="welcome-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          onClick={close}
          className="fixed inset-0 z-[80] flex items-center justify-center bg-ink/30 backdrop-blur-sm cursor-pointer"
          data-testid="welcome-micro-delight"
        >
          <motion.div
            initial={{ scale: 0.9, y: 16, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.95, y: 12, opacity: 0 }}
            transition={{ type: "spring", stiffness: 220, damping: 20, delay: 0.05 }}
            onClick={(e) => e.stopPropagation()}
            className="relative mx-4 max-w-md w-full rounded-[2rem] bg-paper border border-border shadow-floating p-8 text-center overflow-hidden"
            data-testid="welcome-card"
          >
            <div className="pointer-events-none absolute -top-20 -right-16 w-64 h-64 rounded-full bg-mustard/40 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-20 -left-16 w-72 h-72 rounded-full bg-terracotta/25 blur-3xl" />

            <motion.img
              src="/mangocubs-logo.png?v=20260514c"
              alt="MangoCubs"
              className="relative mx-auto h-32 w-32 object-contain select-none"
              draggable={false}
              initial={{ y: -8, rotate: -8 }}
              animate={{ y: [-8, 4, -4, 0], rotate: [-8, 6, -3, 0] }}
              transition={{ duration: 1.1, ease: "easeOut" }}
              data-testid="welcome-mango"
            />

            <motion.h2
              className="relative mt-4 font-heading text-2xl sm:text-3xl leading-tight tracking-tight text-ink"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.4 }}
              data-testid="welcome-greeting"
            >
              {greetingNode}
            </motion.h2>

            <motion.p
              className="relative mt-3 text-ink-soft text-sm sm:text-base"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.4 }}
              data-testid="welcome-tip"
            >
              {(() => {
                switch (user.role) {
                  case "parent":
                    return "Tap the mango at the top-left any time to come home. We'll keep your child's day warm and well-organised.";
                  case "teacher":
                    return "Your day is laid out and ready. Send a photo, mark attendance, share a kind word — your class is waiting.";
                  case "branch_admin":
                    return "Your centre is in great hands. Enrolments, fees, and parents — all one click away.";
                  case "hq_admin":
                  case "super_admin":
                    return "Every centre, every parent, every cub — neatly in one playful operating system.";
                  case "auditor":
                    return "Compliance and finance views are read-only and audit-ready.";
                  case "non_teaching":
                    return "Gate logs, daily routines, parent updates — all the tools you need are in your sidebar.";
                  default:
                    return "Have a warm, well-organised day.";
                }
              })()}
            </motion.p>

            <button
              type="button"
              onClick={close}
              className="relative mt-6 px-6 py-2.5 rounded-full bg-terracotta text-white font-semibold hover:bg-terracotta-hover transition-colors shadow-soft"
              data-testid="welcome-dismiss-btn"
            >
              Let's go ✨
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
